import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Navbar from "../../../components/Navbar";
import API from "../../../api/api";
import SearchableSelect from "../../../components/common/SearchableSelect";
import { toastMessageAlert } from "../../../utils/alert";

const normalizeType = (value) =>
  String(value || "")
    .toUpperCase()
    .replace(/\s*\/\s*/g, "_")
    .replace(/\s+/g, "_")
    .trim();
const isDistanceLikeType = (type) =>
  ["DISTANCE", "DISTANCE_HEIGHT", "MAX_WEIGHT"].includes(normalizeType(type));
const rowKey = (row) => `${row.registration_id}_${row.game_id || 0}`;
const toNumeric = (value) => {
  const n = Number(String(value ?? "").trim());
  return Number.isFinite(n) ? n : 0;
};
const formatScore = (value, decimals = 3) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  const factor = 10 ** decimals;
  const rounded = Math.round((n + Number.EPSILON) * factor) / factor;
  if (Math.abs(rounded - Math.round(rounded)) < 1e-9) return String(Math.round(rounded));
  return String(rounded);
};
const pointDistance = (a, b) => {
  if (!a || !b) return 0;
  return Math.hypot((b.x || 0) - (a.x || 0), (b.y || 0) - (a.y || 0));
};
const normalizeText = (value) => String(value ?? "").trim().toLowerCase();
const isBlank = (value) =>
  value === null ||
  value === undefined ||
  (typeof value === "string" && value.trim() === "");
const weightToGrams = (value) => {
  const raw = String(value ?? "").trim().toLowerCase();
  if (!raw) return NaN;
  const kg = raw.match(/([0-9]+(?:\.[0-9]+)?)\s*kg/);
  if (kg) return Number(kg[1]) * 1000;
  const gr = raw.match(/([0-9]+(?:\.[0-9]+)?)\s*g(?:r)?/);
  if (gr) return Number(gr[1]);
  return NaN;
};
const normalizeGenderToken = (value) => {
  const raw = String(value || "").toLowerCase().trim();
  if (raw === "male" || raw === "men" || raw === "m") return "male";
  if (raw === "female" || raw === "women" || raw === "f") return "female";
  return "all";
};
const isParaAthleticsSport = (sportName) =>
  String(sportName || "")
    .toLowerCase()
    .includes("athlet");
const toUniqueClassificationIds = (value) =>
  Array.from(
    new Set(
      (Array.isArray(value) ? value : [])
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0)
        .map((id) => String(id))
    )
  );
const generatedSheetClassificationText = (row) => {
  const names = (Array.isArray(row?.classification_names) ? row.classification_names : [])
    .map((name) => String(name || "").trim())
    .filter(Boolean);
  const ids = toUniqueClassificationIds(row?.classification_ids);
  const includeUnclassified = Boolean(row?.include_unclassified);
  const base = names.length
    ? names.join(", ")
    : (ids.length ? ids.map((id) => `Class ${id}`).join(", ") : "All classifications");
  return includeUnclassified ? `${base} + Missed / New Player` : base;
};
const pickGeneratedSheetForResults = (rows, games, activeGender) => {
  const list = Array.isArray(rows) ? rows : [];
  if (!list.length) return null;
  const availableGameIds = new Set(
    (Array.isArray(games) ? games : [])
      .map((g) => String(g?.id || "").trim())
      .filter(Boolean)
  );
  const filteredByGame = list.filter((row) => {
    const gameId = Number(row?.game_id || 0);
    if (gameId <= 0) return true;
    if (!availableGameIds.size) return false;
    return availableGameIds.has(String(gameId));
  });
  if (!filteredByGame.length) return null;
  const wantedGender = normalizeGenderToken(activeGender);
  if (wantedGender === "all") {
    return filteredByGame.find((row) => normalizeGenderToken(row?.gender) === "all") || filteredByGame[0];
  }
  return (
    filteredByGame.find((row) => normalizeGenderToken(row?.gender) === wantedGender) ||
    filteredByGame.find((row) => normalizeGenderToken(row?.gender) === "all") ||
    filteredByGame[0]
  );
};
const formatGeneratedUpdatedAt = (value) => {
  const date = new Date(String(value || ""));
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
};
const buildWeightSummary = (rows) => {
  const byClass = new Map();
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const weight = String(row?.implement_weight || "").trim();
    if (!weight) return;
    const className = String(row?.classification_name || row?.classification || "").trim() || "Unclassified";
    if (!byClass.has(className)) byClass.set(className, new Set());
    byClass.get(className).add(weight);
  });

  if (!byClass.size) return "";
  const classNames = Array.from(byClass.keys()).sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" })
  );

  if (classNames.length === 1) {
    return Array.from(byClass.get(classNames[0]) || []).join(", ");
  }

  const parts = [];
  classNames.forEach((className) => {
    const weights = Array.from(byClass.get(className) || []);
    weights.forEach((weight) => {
      parts.push(`${weight} (${className})`);
    });
  });
  return parts.join(", ");
};
const CAMERA_PRESETS = {
  balanced: { width: 1280, height: 720, bitrate: 1_600_000, label: "Balanced (720p)" },
  quality: { width: 1920, height: 1080, bitrate: 2_600_000, label: "High Quality (1080p)" },
  compact: { width: 960, height: 540, bitrate: 950_000, label: "Compact (540p)" },
};
const slugifyFileToken = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48) || "na";
const pickRecordingMimeType = () => {
  if (typeof MediaRecorder === "undefined" || typeof MediaRecorder.isTypeSupported !== "function") {
    return "";
  }
  const candidates = [
    "video/webm;codecs=vp9",
    "video/webm;codecs=vp8",
    "video/webm",
    "video/mp4",
  ];
  return candidates.find((type) => MediaRecorder.isTypeSupported(type)) || "";
};

const PointsResults = () => {
  const navigate = useNavigate();
  const { event_id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialGender = useMemo(() => {
    const g = (searchParams.get("gender") || "all").toLowerCase();
    return ["all", "male", "female"].includes(g) ? g : "all";
  }, [searchParams]);
  const [gender, setGender] = useState(initialGender);
  const date = searchParams.get("date") || "";
  const gameDayContextQuery = useMemo(() => {
    const q = new URLSearchParams();
    if (date) q.set("date", date);
    if (event_id) q.set("event_id", String(event_id));
    q.set("gender", gender || "all");
    return q.toString();
  }, [date, event_id, gender]);
  const activeGenderLabel = gender === "male" ? "Male" : gender === "female" ? "Female" : "All";
  const [players, setPlayers] = useState([]);
  const [games, setGames] = useState([]);
  const [eventMeta, setEventMeta] = useState(null);
  const [selectedGameId, setSelectedGameId] = useState("");
  const [selectedClassificationIds, setSelectedClassificationIds] = useState([]);
  const [mixClassificationsForCompute, setMixClassificationsForCompute] = useState(false);
  const [editCombinedSelection, setEditCombinedSelection] = useState(false);
  const [appliedGeneratedSheet, setAppliedGeneratedSheet] = useState(null);
  const [attemptCount, setAttemptCount] = useState(6);
  const [sortKey, setSortKey] = useState("game_classification");
  const [sortDir, setSortDir] = useState("asc");
  const [message, setMessage] = useState("");
  const [exportingKey, setExportingKey] = useState("");
  const [gameMode, setGameMode] = useState("manual");
  const [selectedCompetitorKey, setSelectedCompetitorKey] = useState("");
  const [selectedAttemptNo, setSelectedAttemptNo] = useState(1);
  const [stepKnob, setStepKnob] = useState(2);
  const videoRef = useRef(null);
  const liveCanvasRef = useRef(null);
  const cameraStreamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordingChunksRef = useRef([]);
  const recordingTickerRef = useRef(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraPreset, setCameraPreset] = useState("quality");
  const [cameraCapabilities, setCameraCapabilities] = useState(null);
  const [cameraZoom, setCameraZoom] = useState(1);
  const [aiAssistMode, setAiAssistMode] = useState(true);
  const [recordingActive, setRecordingActive] = useState(false);
  const [recordingElapsedSec, setRecordingElapsedSec] = useState(0);
  const [capturedFrame, setCapturedFrame] = useState("");
  const [annotationMode, setAnnotationMode] = useState("calibration");
  const [calibrationPoints, setCalibrationPoints] = useState([]);
  const [measurementPoints, setMeasurementPoints] = useState([]);
  const [referenceDistanceMeters, setReferenceDistanceMeters] = useState("10");
  const [measuredDistance, setMeasuredDistance] = useState("");

  useEffect(() => {
    if (initialGender !== gender) {
      setGender(initialGender);
    }
  }, [initialGender, gender]);

  useEffect(() => {
    if (!message) return;
    toastMessageAlert(message, { timer: 2400 });
  }, [message]);

  useEffect(() => {
    setSelectedGameId("");
    setSelectedClassificationIds([]);
    setMixClassificationsForCompute(false);
    setEditCombinedSelection(false);
    setAppliedGeneratedSheet(null);
    setAttemptCount(6);
  }, [event_id]);

  useEffect(() => {
    let alive = true;
    const loadPageData = async () => {
      if (!event_id) return;

      const loadPlayers = async () => {
        try {
          const res = await API.fetchEventPlayers(event_id, {
            game_types: "POINTS,DISTANCE,DISTANCE_HEIGHT,MAX_WEIGHT",
            gender: gender === "all" ? undefined : gender,
          });
          const data = res?.data || res || [];
          return Array.isArray(data) ? data : [];
        } catch (_error) {
          return [];
        }
      };

      try {
        const [contextResult, generatedResult, playersResult] = await Promise.allSettled([
          API.fetchResultEventContext(event_id),
          API.fetchGeneratedScoreSheets({ event_id: Number(event_id) }),
          loadPlayers(),
        ]);

        if (!alive) return;

        const ctx =
          contextResult.status === "fulfilled"
            ? (contextResult.value?.data || contextResult.value || {})
            : {};
        const list = Array.isArray(ctx?.games) ? ctx.games : [];
        const pointsGames = list.filter((game) => {
          const type = String(game.game_type || "")
            .toUpperCase()
            .replace(/\s*\/\s*/g, "_")
            .replace(/\s+/g, "_");
          return ["POINTS", "DISTANCE", "DISTANCE_HEIGHT", "MAX_WEIGHT"].includes(type);
        });
        const scopedGames = pointsGames.length ? pointsGames : list;
        setEventMeta(ctx?.event || null);
        setGames(scopedGames);

        setPlayers((prev) => {
          const prevMap = new Map(
            (Array.isArray(prev) ? prev : []).map((row) => [
              rowKey(row),
              Array.isArray(row.attempts) ? row.attempts : [],
            ])
          );
          const loadedPlayers = playersResult.status === "fulfilled" ? playersResult.value : [];
          return (Array.isArray(loadedPlayers) ? loadedPlayers : []).map((player) => {
            const oldAttempts = prevMap.get(rowKey(player)) || [];
            const serverAttempts = Array.isArray(player.attempts) ? player.attempts : [];
            const seed = serverAttempts.length ? serverAttempts : oldAttempts;
            return {
              ...player,
              attempts: Array.isArray(seed) ? [...seed] : [],
            };
          });
        });

        const rows = generatedResult.status === "fulfilled" && Array.isArray(generatedResult.value)
          ? generatedResult.value
          : [];
        const picked = pickGeneratedSheetForResults(rows, scopedGames, gender);
        if (!picked) {
          setSelectedGameId("");
          setSelectedClassificationIds([]);
          setMixClassificationsForCompute(false);
          setEditCombinedSelection(false);
          setAppliedGeneratedSheet(null);
          return;
        }

        const generatedClassIds = toUniqueClassificationIds(picked?.classification_ids);
        const scopedGameIds = new Set(
          scopedGames.map((game) => String(game?.id || "").trim()).filter(Boolean)
        );
        const preferredGameId =
          picked?.game_id && scopedGameIds.has(String(picked.game_id)) ? String(picked.game_id) : "";
        const isCombinedMode = String(picked?.mode || "").toLowerCase() === "combined";
        const mixedPossible = isCombinedMode && generatedClassIds.length >= 2;
        const generatedAttemptCount = Math.max(1, Math.min(12, Number(picked?.attempt_count) || 6));
        const selectedGameName =
          scopedGames.find((game) => String(game.id) === String(preferredGameId))?.game_name ||
          (preferredGameId ? `Game ${preferredGameId}` : "All Games");

        setSelectedGameId(preferredGameId);
        setSelectedClassificationIds(generatedClassIds);
        setMixClassificationsForCompute(mixedPossible);
        setEditCombinedSelection(false);
        setAttemptCount(generatedAttemptCount);
        setAppliedGeneratedSheet({
          id: picked?.id ?? null,
          mode: mixedPossible ? "Combined Categories" : "Individual Categories",
          gameName: selectedGameName,
          categoryText: generatedSheetClassificationText(picked),
          totalPlayers: Number.isFinite(Number(picked?.total_players)) ? Number(picked.total_players) : null,
          attemptCount: generatedAttemptCount,
          updatedAt: formatGeneratedUpdatedAt(picked?.updated_at || picked?.created_at),
        });
      } catch (_error) {
        if (!alive) return;
        setEventMeta(null);
        setGames([]);
        setPlayers([]);
        setAppliedGeneratedSheet(null);
      }
    };

    loadPageData();
    return () => {
      alive = false;
    };
  }, [event_id, gender]);

  useEffect(() => {
    setPlayers((prev) =>
      (Array.isArray(prev) ? prev : []).map((row) => {
        const curr = Array.isArray(row.attempts) ? row.attempts : [];
        return {
          ...row,
          attempts: Array.from({ length: attemptCount }, (_, idx) => curr[idx] ?? ""),
        };
      })
    );
    setSelectedAttemptNo((prev) => Math.max(1, Math.min(attemptCount, Number(prev) || 1)));
  }, [attemptCount]);

  const filteredPlayers = players.filter((p) => {
    if (selectedGameId && String(p.game_id) !== String(selectedGameId)) return false;
    if (selectedClassificationIds.length) {
      const cid = String(p.classification_id || "");
      if (!selectedClassificationIds.includes(cid)) return false;
    }
    return true;
  });

  const bestAttemptValue = (row) => {
    const attempts = Array.isArray(row?.attempts) ? row.attempts : [];
    let best = null;
    attempts.forEach((item) => {
      const n = Number(String(item ?? "").trim());
      if (!Number.isFinite(n)) return;
      if (best === null || n > best) best = n;
    });
    return best;
  };

  const sortedPlayers = useMemo(() => {
    const dir = sortDir === "desc" ? -1 : 1;
    const list = [...filteredPlayers];
    return list.sort((a, b) => {
      const compareText = (x, y) => {
        const ax = String(x ?? "").trim();
        const by = String(y ?? "").trim();
        const aBlank = isBlank(ax);
        const bBlank = isBlank(by);
        if (aBlank && bBlank) return 0;
        if (aBlank) return 1;
        if (bBlank) return -1;
        const base = normalizeText(ax).localeCompare(normalizeText(by), undefined, { numeric: true });
        return base * dir;
      };
      const compareNumber = (x, y) => {
        const nx = Number(x);
        const ny = Number(y);
        const xValid = Number.isFinite(nx);
        const yValid = Number.isFinite(ny);
        if (!xValid && !yValid) return 0;
        if (!xValid) return 1;
        if (!yValid) return -1;
        if (nx === ny) return 0;
        const base = nx < ny ? -1 : 1;
        return base * dir;
      };
      const gameCmp = compareText(a.game_name, b.game_name);
      const classCmp = compareText(
        a.classification_name || a.classification,
        b.classification_name || b.classification
      );
      const gameAndClassCmp = gameCmp !== 0 ? gameCmp : classCmp;

      let cmp = 0;
      switch (sortKey) {
        case "game_classification":
          cmp = gameAndClassCmp;
          break;
        case "game":
          cmp = gameCmp !== 0 ? gameCmp : classCmp;
          break;
        case "gender":
          cmp = compareText(a.gender, b.gender);
          break;
        case "district":
          cmp = compareText(a.district, b.district);
          break;
        case "category":
          cmp = compareText(a.age_category, b.age_category);
          break;
        case "classification":
          cmp = classCmp !== 0 ? classCmp : gameCmp;
          break;
        case "weight":
          cmp = compareNumber(weightToGrams(a.implement_weight), weightToGrams(b.implement_weight));
          break;
        case "best":
          cmp = compareNumber(bestAttemptValue(a), bestAttemptValue(b));
          break;
        case "points":
          cmp = compareNumber(a.points_score, b.points_score);
          break;
        case "rank":
          cmp = compareNumber(a.result_position, b.result_position);
          break;
        case "name":
        default:
          cmp = compareText(a.name, b.name);
          break;
      }
      if (cmp !== 0) return cmp;
      if (gameAndClassCmp !== 0) return gameAndClassCmp;
      return compareText(a.name, b.name);
    });
  }, [filteredPlayers, sortDir, sortKey]);

  const gameOptions = [
    ...new Map(
      [
        ...games.map((g) => [String(g.id || ""), g.game_name || `Game ${g.id}`]),
        ...players.map((p) => [String(p.game_id || ""), p.game_name || `Game ${p.game_id}`]),
      ]
    ).entries(),
  ].filter(([id]) => id);
  const classificationMap = useMemo(() => {
    const map = new Map();
    players
      .filter((p) => !selectedGameId || String(p.game_id) === String(selectedGameId))
      .forEach((p) => {
        const id = String(p.classification_id || "");
        if (!id) return;
        const name = p.classification_name || `Class ${id}`;
        if (!map.has(id)) {
          map.set(id, { id, name, count: 0 });
        }
        map.get(id).count += 1;
      });
    return map;
  }, [players, selectedGameId]);
  const classificationOptions = useMemo(
    () => Array.from(classificationMap.values()).sort((a, b) => a.name.localeCompare(b.name)),
    [classificationMap]
  );
  const combinedCategoryOption = useMemo(() => {
    if (!mixClassificationsForCompute || selectedClassificationIds.length < 2) return null;
    const selectedSet = new Set(selectedClassificationIds.map((id) => String(id)));
    const selectedNames = selectedClassificationIds
      .map((id) => classificationMap.get(String(id))?.name || `Class ${id}`);
    const uniqueNames = Array.from(new Set(selectedNames));
    const combinedLabel = uniqueNames.join(" + ");
    const combinedCount = players.filter((p) => {
      if (selectedGameId && String(p.game_id) !== String(selectedGameId)) return false;
      return selectedSet.has(String(p.classification_id || ""));
    }).length;
    return {
      id: "__combined__",
      name: combinedLabel || "Combined Categories",
      details: combinedLabel || "Combined Categories",
      count: combinedCount,
    };
  }, [mixClassificationsForCompute, selectedClassificationIds, classificationMap, players, selectedGameId]);
  const showCombinedAsSingleOption = Boolean(combinedCategoryOption) && !editCombinedSelection;
  const mixedClassificationIdSet = useMemo(
    () => new Set(selectedClassificationIds.map((id) => String(id))),
    [selectedClassificationIds]
  );
  const selectedGame = games.find((g) => String(g.id) === String(selectedGameId)) || null;
  const selectedCompetitor = useMemo(
    () => filteredPlayers.find((row) => rowKey(row) === selectedCompetitorKey) || null,
    [filteredPlayers, selectedCompetitorKey]
  );
  const getClassificationDisplayName = (row) => {
    if (
      mixClassificationsForCompute &&
      mixedClassificationIdSet.size >= 2 &&
      mixedClassificationIdSet.has(String(row?.classification_id || ""))
    ) {
      return combinedCategoryOption?.name || "Combined Categories";
    }
    return row?.classification_name || "Unclassified";
  };
  const scoreGameType = selectedGame?.game_type || selectedCompetitor?.game_type || "";
  const isDistanceMode = isDistanceLikeType(scoreGameType);
  const stepOptions = isDistanceMode ? [0.01, 0.05, 0.1, 0.25, 0.5, 1] : [0.5, 1, 2, 5, 10];
  const effectiveStep = stepOptions[Math.min(stepKnob, stepOptions.length - 1)];
  const currentAttemptIndex = Math.max(0, Math.min(attemptCount - 1, Number(selectedAttemptNo || 1) - 1));
  const selectedAttemptValue = selectedCompetitor?.attempts?.[currentAttemptIndex] ?? "";
  const activeCameraPreset = CAMERA_PRESETS[cameraPreset] || CAMERA_PRESETS.quality;
  const recordingFileBase = useMemo(() => {
    const playerToken = slugifyFileToken(selectedCompetitor?.name || "player");
    const eventToken = slugifyFileToken(eventMeta?.title || `event-${event_id || "na"}`);
    const gameToken = slugifyFileToken(selectedGame?.game_name || selectedCompetitor?.game_name || "game");
    const sourceDate = eventMeta?.date ? new Date(String(eventMeta.date)) : new Date();
    const year = Number.isNaN(sourceDate.getTime()) ? new Date().getFullYear() : sourceDate.getFullYear();
    const round = Math.max(1, Number(selectedAttemptNo || 1));
    return `${playerToken}-${eventToken}-${playerToken}-${gameToken}-${year}-round${round}`;
  }, [selectedCompetitor, eventMeta, event_id, selectedGame, selectedAttemptNo]);

  useEffect(() => {
    if (!mixClassificationsForCompute || selectedClassificationIds.length < 2) {
      setEditCombinedSelection(false);
    }
  }, [mixClassificationsForCompute, selectedClassificationIds]);

  useEffect(() => {
    if (!isDistanceMode && gameMode === "camera") {
      setGameMode("live");
    }
  }, [isDistanceMode, gameMode]);

  useEffect(() => {
    if (!filteredPlayers.length) {
      setSelectedCompetitorKey("");
      return;
    }
    const exists = filteredPlayers.some((row) => rowKey(row) === selectedCompetitorKey);
    if (!exists) setSelectedCompetitorKey(rowKey(filteredPlayers[0]));
  }, [filteredPlayers, selectedCompetitorKey]);

  useEffect(() => {
    if (stepKnob > stepOptions.length - 1) {
      setStepKnob(stepOptions.length - 1);
    }
  }, [stepKnob, stepOptions.length]);

  useEffect(() => {
    const refMeters = Number(referenceDistanceMeters);
    if (
      calibrationPoints.length === 2 &&
      measurementPoints.length === 2 &&
      Number.isFinite(refMeters) &&
      refMeters > 0
    ) {
      const calibrationPx = pointDistance(calibrationPoints[0], calibrationPoints[1]);
      const targetPx = pointDistance(measurementPoints[0], measurementPoints[1]);
      if (calibrationPx > 0) {
        const meters = (targetPx / calibrationPx) * refMeters;
        setMeasuredDistance(formatScore(meters, 3));
        return;
      }
    }
    setMeasuredDistance("");
  }, [calibrationPoints, measurementPoints, referenceDistanceMeters]);

  useEffect(() => {
    if (!capturedFrame || !liveCanvasRef.current) return;
    const canvas = liveCanvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const img = new Image();
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      const drawMarkerSet = (points, color, label) => {
        if (!points?.length) return;
        ctx.save();
        ctx.strokeStyle = color;
        ctx.fillStyle = color;
        ctx.lineWidth = Math.max(2, canvas.width / 500);
        points.forEach((pt, idx) => {
          ctx.beginPath();
          ctx.arc(pt.x, pt.y, Math.max(5, canvas.width / 180), 0, Math.PI * 2);
          ctx.fill();
          ctx.font = `${Math.max(14, canvas.width / 45)}px Arial`;
          ctx.fillText(`${label}${idx + 1}`, pt.x + 8, pt.y - 8);
        });
        if (points.length === 2) {
          ctx.beginPath();
          ctx.moveTo(points[0].x, points[0].y);
          ctx.lineTo(points[1].x, points[1].y);
          ctx.stroke();
        }
        ctx.restore();
      };

      drawMarkerSet(calibrationPoints, "#f59e0b", "C");
      drawMarkerSet(measurementPoints, "#10b981", "M");
    };
    img.src = capturedFrame;
  }, [capturedFrame, calibrationPoints, measurementPoints]);

  useEffect(() => {
    const track = cameraStreamRef.current?.getVideoTracks?.()?.[0];
    if (!track || !track.applyConstraints || !cameraCapabilities?.zoom) return;
    const min = Number(cameraCapabilities.zoom.min ?? 1);
    const max = Number(cameraCapabilities.zoom.max ?? 1);
    const step = Number(cameraCapabilities.zoom.step ?? 0.1) || 0.1;
    const bounded = Math.min(max, Math.max(min, Number(cameraZoom || 1)));
    const snapped = min + Math.round((bounded - min) / step) * step;
    track.applyConstraints({ advanced: [{ zoom: snapped }] }).catch(() => {});
  }, [cameraZoom, cameraCapabilities]);

  useEffect(
    () => () => {
      if (recordingTickerRef.current) {
        clearInterval(recordingTickerRef.current);
        recordingTickerRef.current = null;
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }
      if (cameraStreamRef.current) {
        cameraStreamRef.current.getTracks().forEach((track) => track.stop());
        cameraStreamRef.current = null;
      }
    },
    []
  );

  const updateAttempt = (registrationId, gameId, attemptNo, value) => {
    const copy = players.map((row) => {
      if (
        String(row.registration_id) === String(registrationId) &&
        String(row.game_id) === String(gameId)
      ) {
        const attempts = Array.isArray(row.attempts) ? [...row.attempts] : Array.from({ length: attemptCount }, () => "");
        attempts[attemptNo] = value;
        return { ...row, attempts };
      }
      return row;
    });
    setPlayers(copy);
  };

  const updateSelectedAttempt = (nextValue) => {
    if (!selectedCompetitor) {
      setMessage("Select a competitor first.");
      return;
    }
    updateAttempt(
      selectedCompetitor.registration_id,
      selectedCompetitor.game_id,
      currentAttemptIndex,
      nextValue
    );
  };

  const adjustSelectedAttempt = (direction = 1, multiplier = 1) => {
    if (!selectedCompetitor) {
      setMessage("Select a competitor first.");
      return;
    }
    const current = toNumeric(selectedAttemptValue);
    const delta = direction * effectiveStep * multiplier;
    const next = Math.max(0, current + delta);
    updateSelectedAttempt(formatScore(next, isDistanceMode ? 3 : 2));
  };

  const clearSelectedAttempt = () => {
    updateSelectedAttempt("");
  };

  const copyBestToSelectedAttempt = () => {
    if (!selectedCompetitor) return;
    const best = selectedCompetitor.points_score;
    if (best === null || best === undefined || best === "") {
      setMessage("No computed best score available for selected competitor.");
      return;
    }
    updateSelectedAttempt(String(best));
  };

  const saveRecordedBlob = (blob, baseName) => {
    if (!blob || !blob.size) return;
    const ext = String(blob.type || "").includes("mp4") ? "mp4" : "webm";
    const fileName = `${baseName}.${ext}`;
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 500);
  };

  const stopRecording = () => {
    if (recordingTickerRef.current) {
      clearInterval(recordingTickerRef.current);
      recordingTickerRef.current = null;
    }
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.stop();
    } else {
      setRecordingActive(false);
    }
  };

  const startRecording = () => {
    if (!cameraReady || !cameraStreamRef.current) {
      setMessage("Start camera first, then record.");
      return;
    }
    if (typeof MediaRecorder === "undefined") {
      setMessage("MediaRecorder is not supported in this browser.");
      return;
    }
    if (recordingActive) return;

    try {
      const mimeType = pickRecordingMimeType();
      const options = {
        videoBitsPerSecond: activeCameraPreset.bitrate,
      };
      if (mimeType) options.mimeType = mimeType;

      recordingChunksRef.current = [];
      const recorder = new MediaRecorder(cameraStreamRef.current, options);
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (evt) => {
        if (evt?.data && evt.data.size > 0) {
          recordingChunksRef.current.push(evt.data);
        }
      };
      recorder.onerror = () => {
        setRecordingActive(false);
        setMessage("Recording failed.");
      };
      recorder.onstop = () => {
        const chunks = Array.isArray(recordingChunksRef.current) ? recordingChunksRef.current : [];
        const blobType = recorder.mimeType || (mimeType || "video/webm");
        const blob = new Blob(chunks, { type: blobType });
        setRecordingActive(false);
        if (recordingTickerRef.current) {
          clearInterval(recordingTickerRef.current);
          recordingTickerRef.current = null;
        }
        if (blob.size > 0) {
          saveRecordedBlob(blob, recordingFileBase);
          setMessage(`Recording saved to Downloads as ${recordingFileBase}.`);
        } else {
          setMessage("Recording stopped, but no video was captured.");
        }
      };

      recorder.start(1000);
      setRecordingElapsedSec(0);
      setRecordingActive(true);
      recordingTickerRef.current = setInterval(() => {
        setRecordingElapsedSec((prev) => prev + 1);
      }, 1000);
      setMessage("Recording started.");
    } catch (err) {
      setRecordingActive(false);
      setMessage(err?.message || "Unable to start recording.");
    }
  };

  const startCamera = async () => {
    if (!navigator?.mediaDevices?.getUserMedia) {
      setMessage("Camera API is not supported in this browser.");
      return;
    }
    try {
      stopRecording();
      if (cameraStreamRef.current) {
        cameraStreamRef.current.getTracks().forEach((track) => track.stop());
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: activeCameraPreset.width },
          height: { ideal: activeCameraPreset.height },
          frameRate: { ideal: 30, max: 60 },
        },
        audio: false,
      });
      cameraStreamRef.current = stream;
      const track = stream.getVideoTracks?.()[0];
      if (track?.getCapabilities) {
        const capabilities = track.getCapabilities() || null;
        setCameraCapabilities(capabilities);
        const advanced = [];
        if (Array.isArray(capabilities?.focusMode) && capabilities.focusMode.includes("continuous")) {
          advanced.push({ focusMode: "continuous" });
        }
        if (Array.isArray(capabilities?.exposureMode) && capabilities.exposureMode.includes("continuous")) {
          advanced.push({ exposureMode: "continuous" });
        }
        if (Array.isArray(capabilities?.whiteBalanceMode) && capabilities.whiteBalanceMode.includes("continuous")) {
          advanced.push({ whiteBalanceMode: "continuous" });
        }
        if (advanced.length && track.applyConstraints) {
          await track.applyConstraints({ advanced });
        }
        if (capabilities?.zoom) {
          const minZoom = Number(capabilities.zoom.min ?? 1);
          setCameraZoom(minZoom);
        }
      } else {
        setCameraCapabilities(null);
      }
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCameraReady(true);
      setMessage(`Pro camera started (${activeCameraPreset.label}).`);
    } catch (err) {
      setCameraReady(false);
      setMessage(err?.message || "Unable to start camera.");
    }
  };

  const stopCamera = () => {
    stopRecording();
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach((track) => track.stop());
      cameraStreamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraReady(false);
    setCameraCapabilities(null);
  };

  useEffect(() => {
    if (gameMode !== "camera") {
      if (recordingTickerRef.current) {
        clearInterval(recordingTickerRef.current);
        recordingTickerRef.current = null;
      }
      const recorder = mediaRecorderRef.current;
      if (recorder && recorder.state !== "inactive") {
        recorder.stop();
      }
      if (cameraStreamRef.current) {
        cameraStreamRef.current.getTracks().forEach((track) => track.stop());
        cameraStreamRef.current = null;
      }
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
      setRecordingActive(false);
      setCameraReady(false);
      setCameraCapabilities(null);
    }
  }, [gameMode]);

  const captureFromCamera = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth || !video.videoHeight) {
      setMessage("Camera stream is not ready. Start camera first.");
      return;
    }
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.filter = aiAssistMode ? "contrast(1.06) saturate(1.08) brightness(1.02)" : "none";
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const frame = canvas.toDataURL("image/jpeg", aiAssistMode ? 0.88 : 0.92);
    setCapturedFrame(frame);
    setCalibrationPoints([]);
    setMeasurementPoints([]);
    setMeasuredDistance("");
    setAnnotationMode("calibration");
    setMessage("Frame captured. Mark calibration points, then measurement points.");
  };

  const handleCanvasClick = (event) => {
    if (!liveCanvasRef.current || !capturedFrame) return;
    const canvas = liveCanvasRef.current;
    const rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const x = ((event.clientX - rect.left) / rect.width) * canvas.width;
    const y = ((event.clientY - rect.top) / rect.height) * canvas.height;
    const point = { x, y };
    if (annotationMode === "calibration") {
      setCalibrationPoints((prev) => (prev.length >= 2 ? [point] : [...prev, point]));
      return;
    }
    setMeasurementPoints((prev) => (prev.length >= 2 ? [point] : [...prev, point]));
  };

  const applyMeasuredDistance = () => {
    if (!measuredDistance) {
      setMessage("Mark two calibration points and two measurement points first.");
      return;
    }
    updateSelectedAttempt(measuredDistance);
    setMessage(`Measured distance ${measuredDistance} applied to selected attempt.`);
  };

  const persistAttempts = async () => {
    const results = [];
    filteredPlayers.forEach((p) => {
      const resolvedGameId = Number(p.game_id || selectedGameId || 0);
      p.attempts.forEach((score, i) => {
        const normalizedScore = String(score ?? "").trim();
        if (normalizedScore !== "" && resolvedGameId > 0) {
          results.push({
            event_reg_id: p.registration_id,
            registration_id: p.registration_id,
            game_id: resolvedGameId,
            selected_game_id: Number(selectedGameId || 0) || null,
            attempt_no: i + 1,
            score: normalizedScore,
          });
        }
      });
    });

    if (!results.length) {
      setMessage("Enter at least one attempt before saving/computing.");
      return false;
    }

    await API.savePointsAttempts({ results });
    return true;
  };

  const saveAttempts = async () => {
    try {
      const ok = await persistAttempts();
      if (ok) setMessage("Attempts saved successfully.");
    } catch (err) {
      setMessage(err?.message || "Save failed.");
    }
  };

  const computeResults = async () => {
    try {
      if (!selectedGameId) {
        setMessage("Select a game to compute results.");
        return;
      }
      if (mixClassificationsForCompute && selectedClassificationIds.length < 2) {
        setMessage("Select at least 2 classifications to mix.");
        return;
      }

      const saved = await persistAttempts();
      if (!saved) return;

      await API.computePositions({
        event_id: Number(event_id),
        game_id: Number(selectedGameId),
        mix_classifications: mixClassificationsForCompute,
        mix_classification_ids: mixClassificationsForCompute
          ? selectedClassificationIds.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0)
          : [],
      });
      const res = await API.fetchEventPlayers(event_id, {
        game_types: "POINTS,DISTANCE,DISTANCE_HEIGHT,MAX_WEIGHT",
        gender: gender === "all" ? undefined : gender,
      });
      const data = res?.data || res || [];
      const list = Array.isArray(data) ? data : [];
      setPlayers((prev) => {
        const prevMap = new Map(
          (Array.isArray(prev) ? prev : []).map((row) => [
            rowKey(row),
            Array.isArray(row.attempts) ? row.attempts : [],
          ])
        );
        return list.map((p) => {
          const oldAttempts = prevMap.get(rowKey(p)) || [];
          const serverAttempts = Array.isArray(p.attempts) ? p.attempts : [];
          const seed = serverAttempts.length ? serverAttempts : oldAttempts;
          return {
            ...p,
            attempts: Array.from({ length: attemptCount }, (_, idx) => seed[idx] ?? ""),
          };
        });
      });
      setMessage("Positions computed successfully.");
    } catch (err) {
      setMessage(err?.message || "Failed to compute positions.");
    }
  };

  const exportReport = async (format) => {
    setExportingKey(format);
    try {
      await API.downloadResultsReport(event_id, "points", format);
      setMessage(`${format.toUpperCase()} exported successfully!`);
    } catch (err) {
      setMessage(err?.message || `Error exporting ${format.toUpperCase()}`);
    } finally {
      setExportingKey("");
    }
  };

  const handleGenderChange = (nextGender) => {
    const normalized = ["all", "male", "female"].includes(nextGender) ? nextGender : "all";
    setGender(normalized);
    const q = new URLSearchParams(searchParams);
    q.set("gender", normalized);
    setSearchParams(q, { replace: true });
  };

  const toggleSort = (key, defaultDir = "asc") => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDir(defaultDir);
  };

  const sortIcon = (key) => {
    if (sortKey !== key) return "";
    return sortDir === "asc" ? " ▲" : " ▼";
  };

  const printResultsTable = () => {
    if (!sortedPlayers.length) {
      setMessage("No rows available to print.");
      return;
    }

    const escapeHtml = (value) =>
      String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");

    const attemptHeaders = Array.from({ length: attemptCount }, (_, idx) => `<th>Attempt ${idx + 1}</th>`).join("");
    const rowsHtml = sortedPlayers
      .map((row, idx) => {
        const attempts = Array.from({ length: attemptCount }, (_, i) => row?.attempts?.[i] ?? "");
        const attemptsHtml = attempts.map((item) => `<td>${escapeHtml(item)}</td>`).join("");
        const best = bestAttemptValue(row);
        return `
          <tr>
            <td>${idx + 1}</td>
            <td>${escapeHtml(row.name || "-")}</td>
            <td>${escapeHtml(row.gender || "-")}</td>
            <td>${escapeHtml(row.district || "-")}</td>
            <td>${escapeHtml(row.game_name || "-")}</td>
            <td>${escapeHtml(row.age_category || "-")}</td>
            <td>${escapeHtml(getClassificationDisplayName(row))}</td>
            <td>${escapeHtml(row.implement_weight || "-")}</td>
            ${attemptsHtml}
            <td>${best === null ? "" : escapeHtml(formatScore(best, isDistanceMode ? 3 : 2))}</td>
            <td>${escapeHtml(row.points_score ?? "")}</td>
            <td>${escapeHtml(row.result_position ?? "")}</td>
          </tr>
        `;
      })
      .join("");

    const selectedGameName =
      gameOptions.find(([id]) => String(id) === String(selectedGameId))?.[1] || "All Games";
    const isParaAthletics = isParaAthleticsSport(eventMeta?.sport_name);
    const eventTitle = eventMeta?.title || (isParaAthletics ? "POINTS RESULTS" : `Event #${event_id}`);
    const descriptionText =
      selectedGame?.game_narration ||
      eventMeta?.description ||
      "-";
    const eventDate = date || eventMeta?.date || "-";
    const venue = eventMeta?.venue || eventMeta?.location || "-";
    const weightSummary = buildWeightSummary(sortedPlayers);
    const leftMetaText = `${String(activeGenderLabel || "All").toUpperCase()} ${String(selectedGameName || "ALL GAMES").toUpperCase()}`.trim();

    const html = `
      <!doctype html>
      <html>
      <head>
        <meta charset="utf-8" />
        <title>Points Results</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 16px; color: #111827; }
          h1 { margin: 0 0 4px; font-size: 22px; text-align: center; font-weight: 800; }
          h2 { margin: 0 0 8px; font-size: 18px; text-align: center; font-weight: 700; }
          .meta { font-size: 12px; color: #374151; margin-bottom: 6px; text-align: center; }
          .meta strong { color: #111827; }
          .meta-wrap { margin-bottom: 12px; padding: 2px 0; }
          .meta-row { display: flex; justify-content: space-between; align-items: center; margin: 2px 0 8px; font-size: 12px; color: #1f2937; border-bottom: 1px solid #d1d5db; padding-bottom: 3px; gap: 10px; font-weight: 700; }
          .meta-row-right { text-align: right; }
          table { width: 100%; border-collapse: collapse; font-size: 11px; }
          th, td { border: 1px solid #d1d5db; padding: 5px; text-align: left; }
          th { background: #f3f4f6; }
          td:nth-child(1), td:last-child, td:nth-last-child(2), td:nth-last-child(3) { text-align: center; }
        </style>
      </head>
      <body>
        <h1>PARASPORTS ASSOCIATION OF ANDHRA PRADESH</h1>
        <h2>${escapeHtml(eventTitle)}</h2>
        <div class="meta-wrap">
          <div class="meta"><strong>Description:</strong> ${escapeHtml(descriptionText)}</div>
          <div class="meta"><strong>Date:</strong> ${escapeHtml(eventDate)}</div>
          <div class="meta"><strong>Venue:</strong> ${escapeHtml(venue)}</div>
        </div>
        <div class="meta-row"><span>${escapeHtml(leftMetaText)}</span><span class="meta-row-right">${escapeHtml(`Weight: ${weightSummary || "-"}`)}</span></div>
        <table>
          <thead>
            <tr>
              <th>S No</th>
              <th>Player</th>
              <th>Gender</th>
              <th>District</th>
              <th>Game</th>
              <th>Category</th>
              <th>Classification</th>
              <th>Weight</th>
              ${attemptHeaders}
              <th>Best</th>
              <th>Points</th>
              <th>Rank</th>
            </tr>
          </thead>
          <tbody>${rowsHtml}</tbody>
        </table>
      </body>
      </html>
    `;

    const frame = document.createElement("iframe");
    frame.style.position = "fixed";
    frame.style.right = "0";
    frame.style.bottom = "0";
    frame.style.width = "0";
    frame.style.height = "0";
    frame.style.border = "0";
    document.body.appendChild(frame);
    const doc = frame.contentWindow?.document;
    if (!doc) {
      document.body.removeChild(frame);
      setMessage("Unable to open print preview.");
      return;
    }
    doc.open();
    doc.write(html);
    doc.close();
    setTimeout(() => {
      frame.contentWindow?.focus();
      frame.contentWindow?.print();
      setTimeout(() => {
        document.body.removeChild(frame);
      }, 250);
    }, 220);
  };

  const recordingClock = `${String(Math.floor(recordingElapsedSec / 60)).padStart(2, "0")}:${String(
    recordingElapsedSec % 60
  ).padStart(2, "0")}`;

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-6xl mx-auto p-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-3xl font-bold text-gray-900">Points Results</h1>
            <div className="flex gap-2 items-center">
              <label className="text-sm text-gray-700">
                Gender
                <select
                  value={gender}
                  onChange={(e) => handleGenderChange(e.target.value)}
                  className="ml-2 rounded border border-gray-300 px-2 py-1"
                >
                  <option value="all">All</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </label>
              <button
                onClick={() => navigate(`/admin/game-day?${gameDayContextQuery}`)}
                className="text-sm px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300"
              >
                Back to Game Day
              </button>
            </div>
          </div>
          <p className="text-xs text-blue-700 mb-3">Active Filter: Gender = {activeGenderLabel}</p>

          {message ? <div className="mb-3 text-sm bg-gray-100 p-3 rounded-lg">{message}</div> : null}

          <div className="mb-4 rounded-xl border border-slate-200 bg-white p-3 flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Game Mode</span>
            <button
              type="button"
              onClick={() => setGameMode("manual")}
              className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
                gameMode === "manual" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              Manual
            </button>
            <button
              type="button"
              onClick={() => setGameMode("live")}
              className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
                gameMode === "live" ? "bg-emerald-700 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              Live
            </button>
            {isDistanceMode ? (
              <button
                type="button"
                onClick={() => setGameMode("camera")}
                className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
                  gameMode === "camera" ? "bg-indigo-700 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
              >
                Camera
              </button>
            ) : null}
          </div>

          {gameMode === "live" ? (
            <div className="mb-4 rounded-2xl border border-slate-200 bg-white shadow p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-lg font-semibold text-slate-900">Live Score Pad</h2>
              <div className="text-xs sm:text-sm text-slate-600">
                Selected: <span className="font-semibold text-slate-800">{selectedCompetitor?.name || "-"}</span>
              </div>
            </div>
            <div className="mt-3 grid lg:grid-cols-2 gap-4">
              <div className="rounded-2xl border border-slate-200 p-4 bg-slate-50">
                <div className="text-xs uppercase tracking-wide text-slate-500">Current Attempt</div>
                <div className="mt-1 font-mono text-4xl sm:text-5xl text-slate-900">
                  {selectedAttemptValue === "" ? "-" : selectedAttemptValue}
                </div>
                <div className="mt-3 grid sm:grid-cols-2 gap-3">
                  <label className="text-sm text-slate-700">
                    Attempt #
                    <input
                      type="range"
                      min={1}
                      max={attemptCount}
                      step={1}
                      value={selectedAttemptNo}
                      onChange={(e) => setSelectedAttemptNo(Number(e.target.value))}
                      className="w-full mt-1"
                    />
                    <span className="text-xs text-slate-500">Attempt {selectedAttemptNo}</span>
                  </label>
                  <label className="text-sm text-slate-700">
                    Step Knob
                    <input
                      type="range"
                      min={0}
                      max={Math.max(0, stepOptions.length - 1)}
                      step={1}
                      value={Math.min(stepKnob, stepOptions.length - 1)}
                      onChange={(e) => setStepKnob(Number(e.target.value))}
                      className="w-full mt-1"
                    />
                    <span className="text-xs text-slate-500">
                      Step: {effectiveStep} {isDistanceMode ? "m/kg units" : "pts"}
                    </span>
                  </label>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <button
                  type="button"
                  onClick={() => adjustSelectedAttempt(-1, 5)}
                  className="rounded-2xl px-4 py-3 text-sm sm:text-base font-bold text-white bg-gradient-to-b from-rose-400 to-rose-700 shadow-[0_5px_0_#be123c,0_12px_24px_rgba(190,24,93,0.35)] hover:brightness-110 active:translate-y-[2px]"
                >
                  -5x
                </button>
                <button
                  type="button"
                  onClick={() => adjustSelectedAttempt(-1, 1)}
                  className="rounded-2xl px-4 py-3 text-sm sm:text-base font-bold text-white bg-gradient-to-b from-rose-400 to-rose-700 shadow-[0_5px_0_#be123c,0_12px_24px_rgba(190,24,93,0.35)] hover:brightness-110 active:translate-y-[2px]"
                >
                  -1x
                </button>
                <button
                  type="button"
                  onClick={clearSelectedAttempt}
                  className="rounded-2xl px-4 py-3 text-sm sm:text-base font-bold text-white bg-gradient-to-b from-amber-400 to-amber-700 shadow-[0_5px_0_#b45309,0_12px_24px_rgba(180,83,9,0.35)] hover:brightness-110 active:translate-y-[2px]"
                >
                  Clear
                </button>
                <button
                  type="button"
                  onClick={() => adjustSelectedAttempt(1, 1)}
                  className="rounded-2xl px-4 py-3 text-sm sm:text-base font-bold text-white bg-gradient-to-b from-emerald-400 to-emerald-700 shadow-[0_5px_0_#0f766e,0_12px_24px_rgba(15,118,110,0.35)] hover:brightness-110 active:translate-y-[2px]"
                >
                  +1x
                </button>
                <button
                  type="button"
                  onClick={() => adjustSelectedAttempt(1, 5)}
                  className="rounded-2xl px-4 py-3 text-sm sm:text-base font-bold text-white bg-gradient-to-b from-emerald-400 to-emerald-700 shadow-[0_5px_0_#0f766e,0_12px_24px_rgba(15,118,110,0.35)] hover:brightness-110 active:translate-y-[2px]"
                >
                  +5x
                </button>
                <button
                  type="button"
                  onClick={copyBestToSelectedAttempt}
                  className="rounded-2xl px-4 py-3 text-sm sm:text-base font-bold text-white bg-gradient-to-b from-sky-500 to-blue-700 shadow-[0_5px_0_#1d4ed8,0_12px_24px_rgba(30,64,175,0.35)] hover:brightness-110 active:translate-y-[2px]"
                >
                  Use Best
                </button>
              </div>
            </div>
            <div className="mt-3 text-xs text-slate-500">
              Tip: click any row or attempt box below. This pad updates that selected attempt instantly.
            </div>
          </div>
          ) : null}

          {gameMode === "camera" && isDistanceMode ? (
            <div className="mb-4 rounded-2xl border border-slate-200 bg-white shadow p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-lg font-semibold text-slate-900">Distance Camera (Assisted)</h2>
                <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-600">
                  <span>Capture For:</span>
                  <select
                    value={selectedCompetitorKey}
                    onChange={(e) => setSelectedCompetitorKey(e.target.value)}
                    className="rounded border border-slate-300 px-2 py-1 text-slate-800 min-w-[200px]"
                  >
                    {sortedPlayers.map((row) => {
                      const key = rowKey(row);
                      return (
                        <option key={key} value={key}>
                          {`${row.name || "Player"} (${row.game_name || "-"})`}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>
              <div className="mt-3 grid lg:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div className="grid sm:grid-cols-2 gap-2">
                    <label className="text-sm text-slate-700">
                      Camera Profile
                      <select
                        value={cameraPreset}
                        onChange={(e) => setCameraPreset(e.target.value)}
                        className="mt-1 w-full rounded border border-slate-300 px-2 py-1"
                        disabled={cameraReady}
                      >
                        {Object.entries(CAMERA_PRESETS).map(([key, preset]) => (
                          <option key={key} value={key}>
                            {preset.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="text-sm text-slate-700 flex items-end gap-2 pb-1">
                      <input
                        type="checkbox"
                        checked={aiAssistMode}
                        onChange={(e) => setAiAssistMode(e.target.checked)}
                      />
                      AI Assist (clarity boost)
                    </label>
                  </div>
                  {cameraCapabilities?.zoom ? (
                    <label className="text-sm text-slate-700 block">
                      Zoom
                      <input
                        type="range"
                        min={Number(cameraCapabilities.zoom.min ?? 1)}
                        max={Number(cameraCapabilities.zoom.max ?? 1)}
                        step={Number(cameraCapabilities.zoom.step ?? 0.1) || 0.1}
                        value={cameraZoom}
                        onChange={(e) => setCameraZoom(Number(e.target.value))}
                        className="w-full mt-1"
                        disabled={!cameraReady}
                      />
                    </label>
                  ) : null}
                  <video
                    ref={videoRef}
                    className="w-full rounded-xl border border-slate-200 bg-black aspect-video object-cover"
                    playsInline
                    muted
                    autoPlay
                  />
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={startCamera}
                      className="px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700"
                    >
                      Start Pro Camera
                    </button>
                    <button
                      type="button"
                      onClick={captureFromCamera}
                      disabled={!cameraReady}
                      className="px-3 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:bg-gray-400"
                    >
                      Capture Frame
                    </button>
                    <button
                      type="button"
                      onClick={stopCamera}
                      className="px-3 py-2 rounded-lg bg-rose-600 text-white text-sm font-semibold hover:bg-rose-700"
                    >
                      Stop Camera
                    </button>
                    <button
                      type="button"
                      onClick={recordingActive ? stopRecording : startRecording}
                      disabled={!cameraReady}
                      className={`px-3 py-2 rounded-lg text-white text-sm font-semibold disabled:bg-gray-400 ${
                        recordingActive ? "bg-rose-700 hover:bg-rose-800" : "bg-indigo-700 hover:bg-indigo-800"
                      }`}
                    >
                      {recordingActive ? `Stop Recording (${recordingClock})` : "Start Recording"}
                    </button>
                  </div>
                  <p className="text-xs text-slate-500">
                    Recording is auto-downloaded to your browser Downloads folder as{" "}
                    <span className="font-semibold">{recordingFileBase}.webm</span>.
                  </p>
                </div>
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-2 items-end">
                    <label className="text-sm text-slate-700">
                      Known Reference (m)
                      <input
                        type="number"
                        min="0.1"
                        step="0.1"
                        value={referenceDistanceMeters}
                        onChange={(e) => setReferenceDistanceMeters(e.target.value)}
                        className="mt-1 w-36 border rounded px-2 py-1"
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() => setAnnotationMode("calibration")}
                      className={`px-3 py-2 rounded-lg text-sm font-semibold ${
                        annotationMode === "calibration"
                          ? "bg-amber-600 text-white"
                          : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                      }`}
                    >
                      Mark Calibration
                    </button>
                    <button
                      type="button"
                      onClick={() => setAnnotationMode("measurement")}
                      className={`px-3 py-2 rounded-lg text-sm font-semibold ${
                        annotationMode === "measurement"
                          ? "bg-emerald-700 text-white"
                          : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                      }`}
                    >
                      Mark Throw Distance
                    </button>
                  </div>
                  <canvas
                    ref={liveCanvasRef}
                    onClick={handleCanvasClick}
                    className="w-full rounded-xl border border-slate-200 bg-slate-100 aspect-video cursor-crosshair"
                  />
                  <div className="flex flex-wrap gap-2 items-center">
                    <button
                      type="button"
                      onClick={() => {
                        setCalibrationPoints([]);
                        setMeasurementPoints([]);
                        setMeasuredDistance("");
                      }}
                      className="px-3 py-2 rounded-lg bg-slate-100 text-slate-700 text-sm font-semibold hover:bg-slate-200"
                    >
                      Clear Marks
                    </button>
                    <button
                      type="button"
                      onClick={applyMeasuredDistance}
                      disabled={!measuredDistance}
                      className="px-3 py-2 rounded-lg bg-indigo-700 text-white text-sm font-semibold hover:bg-indigo-800 disabled:bg-gray-400"
                    >
                      Apply Measured Distance
                    </button>
                    <div className="text-sm text-slate-700">
                      Measured: <span className="font-semibold">{measuredDistance || "-"}</span> m
                    </div>
                  </div>
                  <p className="text-xs text-slate-500">
                    Calibration: click 2 points on a known-length line. Measurement: click release point and landing point.
                  </p>
                </div>
              </div>
            </div>
          ) : null}

          <div className="overflow-x-auto bg-white rounded-xl shadow">
            <div className="p-3 border-b bg-gray-50">
              <div className="flex gap-2 flex-wrap">
                <SearchableSelect
                  value={selectedGameId}
                  onChange={(e) => {
                    setSelectedGameId(e.target.value);
                    setSelectedClassificationIds([]);
                    setEditCombinedSelection(false);
                  }}
                  className="border p-2 rounded"
                >
                  <option value="">All Games</option>
                  {gameOptions.map(([id, name]) => (
                    <option key={id} value={id}>{name}</option>
                  ))}
                </SearchableSelect>
                <label className="flex items-center gap-2 text-sm px-2">
                  Attempts
                  <input
                    type="number"
                    min="1"
                    max="12"
                    value={attemptCount}
                    onChange={(e) => setAttemptCount(Math.max(1, Math.min(12, Number(e.target.value) || 1)))}
                    className="w-20 rounded border border-slate-300 px-2 py-1"
                  />
                </label>
                <label className="flex items-center gap-2 text-sm px-2">
                  <input
                    type="checkbox"
                    checked={mixClassificationsForCompute}
                    onChange={(e) => {
                      setMixClassificationsForCompute(e.target.checked);
                      setEditCombinedSelection(false);
                    }}
                  />
                  Mix selected classifications for compute
                </label>
              </div>
              <div className="mt-2 grid sm:grid-cols-2 lg:grid-cols-4 gap-2 text-sm">
                {showCombinedAsSingleOption && combinedCategoryOption ? (
                  <label className="sm:col-span-2 lg:col-span-4 flex items-center gap-2 border rounded px-2 py-1 bg-white">
                    <input
                      type="checkbox"
                      checked={selectedClassificationIds.length > 0}
                      onChange={(e) => {
                        if (!e.target.checked) {
                          setSelectedClassificationIds([]);
                          setEditCombinedSelection(true);
                        }
                      }}
                    />
                    <span>{combinedCategoryOption.name} ({combinedCategoryOption.count})</span>
                  </label>
                ) : (
                  classificationOptions.map((c) => {
                    const checked = selectedClassificationIds.includes(c.id);
                    return (
                      <label key={c.id} className="flex items-center gap-2 border rounded px-2 py-1 bg-white">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            setSelectedClassificationIds((prev) => {
                              if (e.target.checked) {
                                return Array.from(new Set([...prev, c.id]));
                              }
                              return prev.filter((x) => x !== c.id);
                            });
                          }}
                        />
                        <span>{c.name} ({c.count})</span>
                      </label>
                    );
                  })
                )}
              </div>
              {combinedCategoryOption ? (
                <div className="mt-2 flex items-center justify-between gap-3">
                  <p className="text-xs text-gray-600 truncate">
                    {combinedCategoryOption.details}
                  </p>
                  <button
                    type="button"
                    onClick={() => setEditCombinedSelection((prev) => !prev)}
                    className="text-xs text-blue-700 hover:text-blue-900 underline"
                  >
                    {showCombinedAsSingleOption ? "Edit Combined Selection" : "Show Single Combined Option"}
                  </button>
                </div>
              ) : null}
              {appliedGeneratedSheet ? (
                <div className="mt-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs text-indigo-900">
                  <span className="font-semibold">Applied from generated score sheet:</span>{" "}
                  {appliedGeneratedSheet.mode} | {appliedGeneratedSheet.gameName} | {appliedGeneratedSheet.categoryText}
                  {appliedGeneratedSheet.totalPlayers !== null ? ` | Total Players: ${appliedGeneratedSheet.totalPlayers}` : ""}
                  {appliedGeneratedSheet.attemptCount ? ` | Attempts: ${appliedGeneratedSheet.attemptCount}` : ""}
                  {appliedGeneratedSheet.updatedAt && appliedGeneratedSheet.updatedAt !== "-"
                    ? ` | Updated: ${appliedGeneratedSheet.updatedAt}`
                    : ""}
                </div>
              ) : null}
              <p className="mt-2 text-xs text-slate-600">
                Showing {sortedPlayers.length} of {players.length} players.
              </p>
            </div>
            <table className="min-w-full">
              <thead>
                <tr>
                  <th>S No</th>
                  <th>
                    <button type="button" onClick={() => toggleSort("name", "asc")} className="font-semibold">
                      Player{sortIcon("name")}
                    </button>
                  </th>
                  <th>
                    <button type="button" onClick={() => toggleSort("gender", "asc")} className="font-semibold">
                      Gender{sortIcon("gender")}
                    </button>
                  </th>
                  <th>
                    <button type="button" onClick={() => toggleSort("district", "asc")} className="font-semibold">
                      District{sortIcon("district")}
                    </button>
                  </th>
                  <th>
                    <button type="button" onClick={() => toggleSort("game", "asc")} className="font-semibold">
                      Game{sortIcon("game")}
                    </button>
                  </th>
                  <th>
                    <button type="button" onClick={() => toggleSort("category", "asc")} className="font-semibold">
                      Category{sortIcon("category")}
                    </button>
                  </th>
                  <th>
                    <button type="button" onClick={() => toggleSort("classification", "asc")} className="font-semibold">
                      Classification{sortIcon("classification")}
                    </button>
                  </th>
                  <th>
                    <button type="button" onClick={() => toggleSort("weight", "asc")} className="font-semibold">
                      Weight{sortIcon("weight")}
                    </button>
                  </th>
                  {Array.from({ length: attemptCount }).map((_, i) => (
                    <th key={i}>
                      Attempt {i + 1}
                    </th>
                  ))}
                  <th>
                    <button type="button" onClick={() => toggleSort("best", "desc")} className="font-semibold">
                      Best{sortIcon("best")}
                    </button>
                  </th>
                  <th>
                    <button type="button" onClick={() => toggleSort("points", "desc")} className="font-semibold">
                      Points{sortIcon("points")}
                    </button>
                  </th>
                  <th>
                    <button type="button" onClick={() => toggleSort("rank", "asc")} className="font-semibold">
                      Rank{sortIcon("rank")}
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedPlayers.map((p, idx) => (
                  <tr
                    key={`${p.registration_id}-${p.game_id}-${idx}`}
                    className={
                      rowKey(p) === selectedCompetitorKey
                        ? "bg-emerald-50 ring-1 ring-emerald-200 cursor-pointer"
                        : "hover:bg-slate-50 cursor-pointer"
                    }
                    onClick={() => setSelectedCompetitorKey(rowKey(p))}
                  >
                    <td>{idx + 1}</td>
                    <td>{p.name}</td>
                    <td>{p.gender || "-"}</td>
                    <td>{p.district || "-"}</td>
                    <td>{p.game_name}</td>
                    <td>{p.age_category || "-"}</td>
                    <td>{getClassificationDisplayName(p)}</td>
                    <td>{p.implement_weight || "-"}</td>
                    {Array.from({ length: attemptCount }).map((_, i) => (
                      <td key={i}>
                        <input
                          value={p?.attempts?.[i] ?? ""}
                          onChange={(e) => updateAttempt(p.registration_id, p.game_id, i, e.target.value)}
                          onFocus={() => {
                            setSelectedCompetitorKey(rowKey(p));
                            setSelectedAttemptNo(i + 1);
                          }}
                          className="w-full rounded-md border border-slate-300 px-2 py-1"
                        />
                      </td>
                    ))}
                    <td>
                      {bestAttemptValue(p) === null
                        ? "-"
                        : formatScore(bestAttemptValue(p), isDistanceMode ? 3 : 2)}
                    </td>
                    <td>{p.points_score ?? "-"}</td>
                    <td>{p.result_position ?? "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex gap-2">
            <button onClick={saveAttempts} className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700">
              Save Attempts
            </button>
            <button onClick={computeResults} className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700">
              Compute Results
            </button>
            <button
              onClick={printResultsTable}
              className="bg-slate-700 text-white px-4 py-2 rounded hover:bg-slate-800"
            >
              Print
            </button>
            <button
              onClick={() => exportReport("pdf")}
              disabled={exportingKey === "pdf"}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:bg-gray-400"
            >
              {exportingKey === "pdf" ? "Exporting..." : "Download PDF"}
            </button>
            <button
              onClick={() => exportReport("excel")}
              disabled={exportingKey === "excel"}
              className="bg-emerald-600 text-white px-4 py-2 rounded hover:bg-emerald-700 disabled:bg-gray-400"
            >
              {exportingKey === "excel" ? "Exporting..." : "Download Excel"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default PointsResults;
